<template>
  <div class="storage-pack-view">
    <div v-if="loading" class="text-center py-3">
      <b-spinner />
    </div>

    <b-alert :show="Boolean(loadError)" variant="danger" class="p-1">{{ loadError }}</b-alert>

    <template v-if="pack">
      <!-- Шапка пакета -->
      <b-card class="mb-1">
        <div class="d-flex align-items-center flex-wrap">
          <h4 class="mb-0 mr-1">{{ pack.name || `Пакет #${pack.id}` }}</h4>
          <b-badge :variant="pack.type === 'imported' ? 'light-primary' : 'light-secondary'" class="mr-1">
            {{ typeLabels[pack.type] || pack.type }}
          </b-badge>
          <span class="mr-1 text-muted">
            ПФ-пакет:
            <template v-if="pack.planfix_pack">{{ pack.planfix_pack.planfix_key }} · {{ pack.planfix_pack.name }}</template>
            <template v-else>—</template>
          </span>
          <span class="mr-1">Премьера: {{ formatDate(pack.premiere_at) }}</span>
          <span class="mr-1">Последний отыгрыш: {{ formatDate(pack.last_played_at) }} ({{ playsCountText }})</span>
          <span class="ml-auto">
            <b-button
              variant="outline-primary"
              size="sm"
              :to="{ name: 'question-storage', query: { tab: 'questions', game_id: String(pack.id) } }"
            >
              Вопросы пакета в хранилище
            </b-button>
          </span>
        </div>
      </b-card>

      <div v-if="!pack.rounds.length" class="text-muted">В пакете нет раундов</div>

      <!-- Раунды → темы → вопросы, только чтение -->
      <b-card
        v-for="round in pack.rounds"
        :key="round.id"
        :title="round.name || `Раунд ${round.number}`"
        class="mb-1"
      >
        <div v-if="!round.topics.length" class="text-muted small">Тем нет</div>
        <div
          v-for="topic in round.topics"
          :key="topic.id"
          class="pack-topic mb-1"
        >
          <div class="d-flex align-items-center mb-50">
            <span class="font-weight-bold mr-50">{{ topic.name || 'Без названия' }}</span>
            <b-badge v-if="topic.mechanic" variant="light-info">{{ topic.mechanic.name }}</b-badge>
            <b-badge v-else variant="light-warning">без механики</b-badge>
          </div>
          <b-table-simple small responsive class="mb-0">
            <b-tbody>
              <b-tr v-for="question in topic.questions" :key="question.id">
                <b-td class="price-cell font-weight-bold">{{ question.price !== null ? question.price : '—' }}</b-td>
                <b-td class="parts-cell">
                  <feather-icon
                    v-for="(type, index) in question.parts_summary.question"
                    :key="`q-${index}`"
                    :icon="partIcon(type)"
                    size="14"
                    class="mr-25"
                  />
                </b-td>
                <b-td>
                  <b-link :to="{ name: 'question-storage-edit', params: { id: question.id } }">
                    {{ question.answer || `Вопрос #${question.id}` }}
                  </b-link>
                </b-td>
                <b-td class="text-muted">
                  <template v-if="question.mechanic && (!topic.mechanic || topic.mechanic.id !== question.mechanic.id)">
                    {{ question.mechanic.name }}
                  </template>
                  <template v-else-if="!question.mechanic">без механики</template>
                </b-td>
                <b-td>{{ entitiesText(question) || '—' }}</b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
          <div v-if="!topic.questions.length" class="text-muted small">Вопросов нет</div>
        </div>
      </b-card>
    </template>
  </div>
</template>

<script>
import {
  BAlert, BBadge, BButton, BCard, BLink, BSpinner, BTableSimple, BTbody, BTd, BTr,
} from 'bootstrap-vue'
import { computed, ref, watch } from '@vue/composition-api'
import store from '@/store'
import questionStorageStoreModule from './questionStorageStoreModule'

const MODULE = 'question-storage'

const pluralize = (count, one, few, many) => {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few

  return many
}

const formatDate = value => {
  if (!value) return '—'
  const [year, month, day] = String(value).slice(0, 10).split('-')

  return `${day}.${month}.${year}`
}

export default {
  components: {
    BAlert, BBadge, BButton, BCard, BLink, BSpinner, BTableSimple, BTbody, BTd, BTr,
  },
  setup(props, { root }) {
    // Модуль стора не снимаем: Vue 2 создаёт компонент следующего роута до уничтожения текущего (см. import/PackImport.vue)
    if (!store.hasModule(MODULE)) store.registerModule(MODULE, questionStorageStoreModule)

    const pack = ref(null)
    const loading = ref(false)
    const loadError = ref('')

    const typeLabels = {
      imported: 'Импорт',
      'builder-music': 'Построитель: музыка',
      'builder-movies': 'Построитель: кино',
      'builder-series': 'Построитель: сериалы',
    }

    const load = () => {
      loading.value = true
      loadError.value = ''
      pack.value = null
      store.dispatch(`${MODULE}/fetchPack`, root.$route.params.id)
        .then(response => { pack.value = response.data.data })
        .catch(error => {
          loadError.value = error && error.response && error.response.status === 404
            ? 'Пакет не найден'
            : 'Не удалось загрузить пакет'
        })
        .finally(() => { loading.value = false })
    }
    load()
    watch(() => root.$route.params.id, (id, previousId) => {
      if (root.$route.name === 'question-storage-pack' && id !== previousId) load()
    })

    const playsCountText = computed(() => {
      const count = (pack.value && pack.value.plays_count) || 0

      return `${count} ${pluralize(count, 'игра', 'игры', 'игр')}`
    })

    const partIcons = {
      audio: 'MusicIcon', image: 'ImageIcon', video: 'FilmIcon', text: 'TypeIcon',
    }
    const partIcon = type => partIcons[type] || 'FileIcon'

    const entitiesText = question => [
      ...question.artists.map(artist => artist.title),
      ...question.movies.map(movie => movie.title_ru),
    ].filter(Boolean).join(', ')

    return {
      pack, loading, loadError, typeLabels, playsCountText, partIcon, entitiesText, formatDate,
    }
  },
}
</script>

<style scoped>
.price-cell {
  width: 60px;
}
.parts-cell {
  width: 80px;
  white-space: nowrap;
}
.pack-topic + .pack-topic {
  border-top: 1px solid #ebe9f1;
  padding-top: 0.75rem;
}
</style>
